import { LayoutComponent } from './layouts/layout/layout.component';
import { VexRoutes } from '@vex/interfaces/vex-route.interface';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';

export const appRoutes: VexRoutes = [
  {
    path: 'login',
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/auth/login-selector/login-selector.component').then(m => m.LoginSelectorComponent),
        data: {
          containerEnabled: true
        }
      },
      {
        path: 'alumno',
        loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent),
        data: {
          containerEnabled: true,
          userType: 'alumno'
        }
      },
      {
        path: 'gestion',
        loadComponent: () => import('./pages/auth/login/login.component').then(m => m.LoginComponent),
        data: {
          containerEnabled: true,
          userType: 'gestion'
        }
      }
    ]
  },
  {
    path: '',
    redirectTo: '/login',
    pathMatch: 'full'
  },
  {
    path: 'alumnos',
    component: LayoutComponent,
    canActivate: [authGuard, roleGuard(['alumno'])],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/social/social-timeline/social-timeline.component').then(m => m.SocialTimelineComponent),
        data: { tab: 0 }
      },
      {
        path: 'mi-horario',
        loadComponent: () => import('./pages/social/social-timeline/social-timeline.component').then(m => m.SocialTimelineComponent),
        data: { tab: 1 }
      },
      {
        path: 'programacion',
        loadComponent: () => import('./pages/social/social-timeline/social-timeline.component').then(m => m.SocialTimelineComponent),
        data: { tab: 2 }
      },
      {
        path: 'mis-practicas',
        loadComponent: () => import('./pages/alumnos/mis-practicas/mis-practicas.component').then(m => m.MisPracticasAlumnoComponent),
        canActivate: [roleGuard(['alumno'])]
      },
      {
        path: 'programacion-practicas',
        loadComponent: () => import('./pages/alumnos/programacion-practicas/programacion-practicas.component').then(m => m.ProgramacionPracticasComponent),
        canActivate: [roleGuard(['alumno'])]
      },
      {
        path: 'mi-examen',
        loadComponent: () => import('./pages/alumno/mi-examen/mi-examen-intro.component').then(m => m.MiExamenIntroComponent),
        canActivate: [roleGuard(['alumno'])]
      },
      {
        path: 'mi-examen/rendir/:intentoId',
        loadComponent: () => import('./pages/alumno/mi-examen/mi-examen-rendir.component').then(m => m.MiExamenRendirComponent),
        canActivate: [roleGuard(['alumno'])]
      },
      {
        path: 'mi-examen/resultado/:intentoId',
        loadComponent: () => import('./pages/alumno/mi-examen/mi-examen-resultado.component').then(m => m.MiExamenResultadoComponent),
        canActivate: [roleGuard(['alumno'])]
      }
    ]
  },
  {
    path: 'colaboradores',
    component: LayoutComponent,
    // 2026-09-15: agregar 'cajero' y 'asesor' — sin ellos, el login los deja
    // atascados: POST /login OK 200, pero al redirigir a /colaboradores/home
    // este guard los rechaza (bug reportado por cliente con NATHALIA cajera —
    // Angie admin sí entraba). Rutas hijas de instructor (mis-practicas,
    // mi-clase-ahora, programacion, consulta-clases) mantienen su guard
    // restringido a instructor+admin abajo.
    canActivate: [authGuard, roleGuard(['instructor', 'administrador', 'cajero', 'asesor', 'empleado'])],
    children: [
      {
        path: '',
        redirectTo: 'home',
        pathMatch: 'full'
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/dashboard/dashboard-analytics.component').then(m => m.DashboardAnalyticsComponent)
      },
      {
        path: 'programacion',
        loadComponent: () => import('./pages/instructor/programacion/programacion.component').then(m => m.ProgramacionComponent),
        canActivate: [roleGuard(['instructor', 'administrador'])]
      },
      {
        path: 'mi-clase-ahora',
        loadComponent: () => import('./pages/instructor/mi-clase-ahora/mi-clase-ahora.component').then(m => m.MiClaseAhoraComponent),
        canActivate: [roleGuard(['instructor', 'administrador'])]
      },
      {
        // Soporte lado colaborador (F1 2026-10-02). Abierto a cualquier
        // cargo != alumno; el backend hace el check.
        path: 'mi-soporte',
        loadComponent: () => import('./pages/soporte/mi-soporte.component').then(m => m.MiSoporteComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado', 'instructor'])]
      },
      {
        path: 'consulta-clases',
        loadComponent: () => import('./pages/instructor/consulta-clases/consulta-clases.component').then(m => m.ConsultaClasesComponent),
        canActivate: [roleGuard(['instructor', 'administrador'])]
      },
      {
        path: 'mis-practicas',
        loadComponent: () => import('./pages/instructor/mis-practicas/mis-practicas.component').then(m => m.MisPracticasInstructorComponent),
        canActivate: [roleGuard(['instructor', 'administrador'])]
      }
    ]
  },
  {
    path: 'clases',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'mi-horario',
        loadComponent: () => import('./pages/clases/mi-horario/mi-horario.component').then(m => m.MiHorarioComponent),
        canActivate: [roleGuard(['alumno'])]
      },
      {
        path: 'programacion',
        loadComponent: () => import('./pages/clases/programacion/programacion.component').then(m => m.ProgramacionComponent),
        canActivate: [roleGuard(['alumno'])]
      }
    ]
  },
  {
    path: 'admin',
    component: LayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'configuracion-bloques',
        pathMatch: 'full'
      },
      {
        path: 'configuracion-cursos',
        loadComponent: () => import('./pages/admin/configuracion-cursos/configuracion-cursos.component').then(m => m.ConfiguracionCursosComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'contratos/config',
        loadComponent: () => import('./pages/admin/contratos-config/contratos-config.component').then(m => m.ContratosConfigComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'configuracion-bloques',
        loadComponent: () => import('./pages/admin/configuracion-bloques/configuracion-bloques.component').then(m => m.ConfiguracionBloquesComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'bloques-teoria',
        loadComponent: () => import('./pages/admin/bloques-teoria/bloques-teoria-admin.component').then(m => m.BloquesTeoriaAdminComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'servicios',
        loadComponent: () => import('./pages/admin/catalogos/servicios/servicios-lista.component').then(m => m.ServiciosListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'paquetes',
        loadComponent: () => import('./pages/admin/catalogos/paquetes/paquetes-lista.component').then(m => m.PaquetesListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'convenios-medicos',
        loadComponent: () => import('./pages/admin/catalogos/convenios-medicos/convenios-medicos-lista.component').then(m => m.ConveniosMedicosListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'vehiculos',
        loadComponent: () => import('./pages/admin/vehiculos/vehiculos-lista.component').then(m => m.VehiculosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'instructores',
        loadComponent: () => import('./pages/admin/instructores/instructores-lista.component').then(m => m.InstructoresListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'terceros',
        loadComponent: () => import('./pages/admin/terceros/terceros-lista.component').then(m => m.TercerosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'asesores',
        loadComponent: () => import('./pages/admin/asesores/asesores-lista.component').then(m => m.AsesoresListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'practicas',
        loadComponent: () => import('./pages/admin/practicas/gestion/gestion-practicas.component').then(m => m.GestionPracticasComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'practicas/agendar',
        loadComponent: () => import('./pages/admin/practicas/grilla-agendar/grilla-agendar.component').then(m => m.GrillaAgendarComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'practicas/instructores',
        loadComponent: () => import('./pages/admin/practicas/instructores/instructores-gestion.component').then(m => m.InstructoresGestionComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'practicas/instructores/:cod/liquidacion',
        loadComponent: () => import('./pages/admin/practicas/liquidacion-instructor/liquidacion-instructor.component').then(m => m.LiquidacionInstructorComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'configuracion-practicas',
        loadComponent: () => import('./pages/admin/configuracion-practicas/configuracion-practicas.component').then(m => m.ConfiguracionPracticasComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'informes',
        loadComponent: () => import('./pages/admin/informes/informes-pyg.component').then(m => m.InformesPygComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'usuarios',
        loadComponent: () => import('./pages/admin/usuarios/usuarios-lista.component').then(m => m.UsuariosListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'usuarios/:doc/permisos',
        loadComponent: () => import('./pages/admin/usuarios/permisos/usuario-permisos.component').then(m => m.UsuarioPermisosComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        // IP whitelist (feature 2026-10-01). Front gatea con admin; backend
        // exige super_admin — un admin comun recibe 403 al listar/modificar.
        path: 'ip-whitelist',
        loadComponent: () => import('./pages/admin/ip-whitelist/ip-whitelist.component').then(m => m.IpWhitelistComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        // Panel admin soporte (feature 2026-10-02 F1). Backend exige
        // require_admin; en front basta con 'administrador'.
        path: 'soporte',
        loadComponent: () => import('./pages/admin/soporte/admin-soporte.component').then(m => m.AdminSoporteComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        // Membresia de soporte (F2a 2026-10-03). Pagos manuales + cortesia.
        path: 'soporte/membresia',
        loadComponent: () => import('./pages/admin/soporte/membresia-admin.component').then(m => m.MembresiaAdminComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'notificaciones',
        loadComponent: () => import('./pages/admin/notificaciones/lista/notificaciones-lista.component').then(m => m.NotificacionesListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'manuales',
        loadComponent: () => import('./pages/admin/manuales/manuales-lista.component').then(m => m.ManualesListaComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'cartera',
        loadComponent: () => import('./pages/admin/cartera/cartera-lista.component').then(m => m.CarteraListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'cartera/:doc',
        loadComponent: () => import('./pages/admin/cartera/cartera-detail.component').then(m => m.CarteraDetailComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'reportes',
        loadComponent: () => import('./pages/admin/reportes/reportes-admin.component').then(m => m.ReportesAdminComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        // Reporte Cartera Estado de pago 50/50 (F4 2026-10-05). Backend
        // gatea con require_permiso(43). Guard de rol admin aqui (patron
        // del repo: "front gatea con admin; backend exige lo fino").
        path: 'reportes/cartera-estado-pago',
        loadComponent: () => import('./pages/admin/reportes/cartera-estado-pago/cartera-estado-pago.component').then(m => m.CarteraEstadoPagoComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'tesoreria/cajas-bancos',
        loadComponent: () => import('./pages/admin/tesoreria/cajas-bancos/cuentas-lista.component').then(m => m.CuentasListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/cajas-bancos/traslados',
        loadComponent: () => import('./pages/admin/tesoreria/cajas-bancos/traslados-lista.component').then(m => m.TrasladosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/recibos-caja',
        loadComponent: () => import('./pages/admin/tesoreria/recibos-caja/recibos-lista.component').then(m => m.RecibosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/recibos-caja/nuevo',
        loadComponent: () => import('./pages/admin/tesoreria/recibos-caja/recibo-wizard.component').then(m => m.ReciboWizardComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/recibos-egresos',
        loadComponent: () => import('./pages/admin/tesoreria/recibos-egresos/egresos-lista.component').then(m => m.EgresosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/recibos-egresos/nuevo',
        loadComponent: () => import('./pages/admin/tesoreria/recibos-egresos/egreso-wizard.component').then(m => m.EgresoWizardComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'tesoreria/turnos',
        loadComponent: () => import('./pages/admin/tesoreria/turnos/turnos-lista.component').then(m => m.TurnosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'empleado'])]
      },
      {
        path: 'cursos',
        loadChildren: () => import('./pages/admin/cursos/cursos.routes').then(m => m.cursosRoutes),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'alumnos',
        loadComponent: () => import('./pages/admin/alumnos/alumnos-lista.component').then(m => m.AlumnosListaComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'alumnos/nuevo',
        redirectTo: 'cursos/nuevo',
        pathMatch: 'full'
      },
      {
        path: 'alumnos/:doc',
        loadComponent: () => import('./pages/admin/alumnos/alumnos-edit.component').then(m => m.AlumnosEditComponent),
        canActivate: [roleGuard(['administrador', 'cajero', 'asesor', 'empleado'])]
      },
      {
        path: 'examenes',
        loadComponent: () => import('./pages/admin/examenes/examen-config.component').then(m => m.ExamenConfigComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'examenes/preguntas',
        loadComponent: () => import('./pages/admin/examenes/preguntas-list.component').then(m => m.PreguntasListComponent),
        canActivate: [roleGuard(['administrador'])]
      },
      {
        path: 'examenes/reportes',
        loadComponent: () => import('./pages/admin/examenes/reportes-examen.component').then(m => m.ReportesExamenComponent),
        canActivate: [roleGuard(['administrador'])]
      }
    ]
  }
];
